import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface AssessmentProgress {
  currentStepIndex: number;
  answers: Record<string, any>;
  email: string;
  name: string;
  subscribe: boolean;
  timestamp: number;
}

interface CompletedAssessment {
  runId: string;
  publicAccessToken: string;
  email: string;
  assessmentType: string;
  timestamp: number;
}

interface AssessmentState {
  // Progress data for each assessment type
  assessments: Record<string, AssessmentProgress>;
  
  // Completed assessments data
  completedAssessments: Record<string, CompletedAssessment>;
  
  // Actions
  saveProgress: (assessmentType: string, data: {
    currentStepIndex: number;
    answers: Record<string, any>;
    email: string;
    name: string;
    subscribe: boolean;
  }) => void;
  
  loadProgress: (assessmentType: string) => {
    currentStepIndex: number;
    answers: Record<string, any>;
    email: string;
    name: string;
    subscribe: boolean;
  } | null;
  
  clearProgress: (assessmentType: string) => void;
  clearAllProgress: () => void;
  hasProgress: (assessmentType: string) => boolean;
  
  // Completed assessment actions
  saveCompletedAssessment: (data: CompletedAssessment) => void;
  getCompletedAssessment: (assessmentType: string) => CompletedAssessment | null;
  clearCompletedAssessment: (assessmentType: string) => void;
}

const EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours

export const useAssessmentStore = create<AssessmentState>()(
  persist(
    (set, get) => ({
      assessments: {},
      completedAssessments: {},
      
      saveProgress: (assessmentType, data) => {
        set((state) => ({
          assessments: {
            ...state.assessments,
            [assessmentType]: {
              ...data,
              timestamp: Date.now(),
            },
          },
        }));
      },
      
      loadProgress: (assessmentType) => {
        const state = get();
        const assessment = state.assessments[assessmentType];
        
        if (!assessment) return null;
        
        // Check if expired
        if (Date.now() - assessment.timestamp > EXPIRY_TIME) {
          // Clear expired data
          state.clearProgress(assessmentType);
          return null;
        }
        
        const { timestamp, ...progressData } = assessment;
        return progressData;
      },
      
      clearProgress: (assessmentType) => {
        set((state) => {
          const { [assessmentType]: _, ...rest } = state.assessments;
          return { assessments: rest };
        });
      },
      
      clearAllProgress: () => {
        set({ assessments: {} });
      },
      
      hasProgress: (assessmentType) => {
        const progress = get().loadProgress(assessmentType);
        return progress !== null && progress.currentStepIndex > 0;
      },
      
      // Completed assessment methods
      saveCompletedAssessment: (data) => {
        set((state) => ({
          completedAssessments: {
            ...state.completedAssessments,
            [data.assessmentType]: {
              ...data,
              timestamp: Date.now(),
            },
          },
        }));
      },
      
      getCompletedAssessment: (assessmentType) => {
        const state = get();
        const completed = state.completedAssessments[assessmentType];
        
        if (!completed) return null;
        
        // Check if expired (15 minutes for security)
        const COMPLETED_EXPIRY = 15 * 60 * 1000; // 15 minutes
        if (Date.now() - completed.timestamp > COMPLETED_EXPIRY) {
          state.clearCompletedAssessment(assessmentType);
          return null;
        }
        
        return completed;
      },
      
      clearCompletedAssessment: (assessmentType) => {
        set((state) => {
          const { [assessmentType]: _, ...rest } = state.completedAssessments;
          return { completedAssessments: rest };
        });
      },
    }),
    {
      name: 'assessment-progress',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        assessments: state.assessments,
        completedAssessments: state.completedAssessments
      }),
    }
  )
);